import PostItems from 'components/containers/PostItems/PostItems';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { CustomArticleSlugTypes } from 'types/global';
import { HeroImageTop } from 'components/containers';
import { PageWrapper } from 'components/presentional';
import { usePages, usePosts } from 'hooks';
import { useRouteTranslations } from 'utils';
import { WPAcfBlog, WPAcfKnowledge } from 'types/acf';
import { WPSimplePage } from 'types/pages';
import { WPSimpleArticle } from 'types/articles';
import { useIntl } from 'react-intl';
import messages from 'messages/base-pl';

interface OwnProps {}

type KnowledgePageProps = Omit<JSX.IntrinsicElements['article'], 'children'> & OwnProps;

const KnowledgePage: FunctionComponent<KnowledgePageProps> = props => {
  const POSTS_PER_PAGE = 6;
  const KNOWLEDGE_PAGE_SLUG_TYPE: CustomArticleSlugTypes = 'knowledge';
  const { translateSlug } = useRouteTranslations();
  const { formatMessage } = useIntl();
  const [perEbooksPage, setPerEbooksPage] = useState(POSTS_PER_PAGE);
  const [perWebinarsPage, setPerWebinarsPage] = useState(POSTS_PER_PAGE);
  const [ebooksItems, setEbooksItems] = useState<WPSimpleArticle<WPAcfBlog>[]>([]);
  const [webinarsItems, setWebinarsItems] = useState<WPSimpleArticle<WPAcfBlog>[]>([]);

  const { data: page, loading } = usePages<WPSimplePage<WPAcfKnowledge>>({ slug: translateSlug(KNOWLEDGE_PAGE_SLUG_TYPE) });

  const { data: webinars, loading: loadingWebinars } = usePosts<WPSimpleArticle<WPAcfBlog>>({
    category_name: 'webinar',
  });

  const { data: ebooks = [], loading: loadingEbooks } = usePosts<WPSimpleArticle<WPAcfBlog>>({
    category_name: 'ebook',
  });

  useEffect(() => {
    if (ebooks && !loadingEbooks) {
      const splittedEbooks = [...ebooks.slice(0, perEbooksPage)];
      if (ebooksItems.length === 0 || splittedEbooks.length !== ebooksItems.length) {
        setEbooksItems(splittedEbooks);
      }
    }
    // eslint-disable-next-line
  }, [ebooks, perEbooksPage, loadingEbooks]);

  useEffect(() => {
    if (webinars && !loadingWebinars) {
      const splittedWebinars = [...webinars.slice(0, perWebinarsPage)];
      if (webinarsItems.length === 0 || splittedWebinars.length !== webinarsItems.length) {
        setWebinarsItems(splittedWebinars);
      }
    }
    // eslint-disable-next-line
  }, [webinars, perWebinarsPage, loadingWebinars]);

  const [customPage] = page || [];

  if (!customPage) {
    return null;
  }

  const {
    acf: { heroImageTop },
  } = customPage;

  return (
    <PageWrapper title={customPage?.yoast_title} meta={customPage?.yoast_meta} loading={loading} name={KNOWLEDGE_PAGE_SLUG_TYPE}>
      <HeroImageTop data={heroImageTop} />
      {loadingEbooks && <div>Loading...</div>}
      {ebooks && !loadingEbooks && (
        <PostItems
          mobile={1}
          tablet={3}
          desktop={3}
          category={translateSlug('ebook')}
          posts={ebooksItems}
          header={formatMessage(messages.ebooks)}
          linkLabel={formatMessage(messages.learn_more)}
          length={perEbooksPage}
          showMore={false}
          loadMore={ebooks.length > perEbooksPage}
          onLoadMore={() => setPerEbooksPage(perEbooksPage + POSTS_PER_PAGE)}
        />
      )}
      {loadingWebinars && <div>Loading...</div>}
      {webinars && !loadingWebinars && (
        <PostItems
          mobile={1}
          tablet={3}
          desktop={3}
          category={translateSlug('webinar')}
          posts={webinarsItems}
          header={formatMessage(messages.webinars)}
          linkLabel={formatMessage(messages.learn_more)}
          length={perWebinarsPage}
          showMore={false}
          loadMore={webinars.length > perWebinarsPage}
          onLoadMore={() => setPerWebinarsPage(perWebinarsPage + POSTS_PER_PAGE)}
        />
      )}
    </PageWrapper>
  );
};

export default KnowledgePage;
